import { ANNOTATION_VALUE } from "@/constants/annotation-values"

import {
  FLOAT_COMPARISON_OPERATOR,
  RANGE_COMPARISON_OPERATOR,
  SET_COMPARISON_OPERATOR,
  type TargetCondition,
} from "@/types/metrics/contracts"

import {
  getMetricType,
  METRIC_TYPE,
  resolveThreshold,
  type MetricThresholdResolutionSource,
  type MetricTypeSource,
  type ResolvedThreshold,
} from "./resolveThreshold"

/**
 * Pass/fail color for a metric value: "success" (green), "danger" (red), or
 * "default" (gray/neutral). Used as a key into the shared yes/no badge class maps.
 */
type MetricColorValue = "default" | "success" | "danger"

/**
 * Determines the color for a YES/NO/UNKNOWN metric value based on target condition.
 *
 * @param value - The metric value ("YES", "NO", or "UNKNOWN")
 * @param targetCondition - Optional target condition with target_values and comparison_operator
 * @returns Color string: "success" (green), "danger" (red), or "default" (gray)
 */
const getStringMetricColor = (
  value: string,
  targetCondition?: TargetCondition,
): MetricColorValue => {
  // UNKNOWN or invalid values are always default
  if (
    value === ANNOTATION_VALUE.UNKNOWN ||
    (value !== ANNOTATION_VALUE.YES && value !== ANNOTATION_VALUE.NO)
  ) {
    return "default"
  }

  // No target condition => use default colors for values
  if (!targetCondition || !("target_values" in targetCondition)) {
    switch (value) {
      case ANNOTATION_VALUE.YES:
        return "success"
      case ANNOTATION_VALUE.NO:
        return "danger"
      default:
        return "default"
    }
  }

  const { target_values, comparison_operator } = targetCondition
  const valueInTarget = target_values.includes(value)

  // "in" => valueInTarget is ideal (success), "nin" => !valueInTarget is ideal (success)
  const isIdeal =
    comparison_operator === SET_COMPARISON_OPERATOR.IN
      ? valueInTarget
      : !valueInTarget

  return isIdeal ? "success" : "danger"
}

const getNumericMetricColor = (
  value: number,
  targetCondition?: TargetCondition,
  floatToBool = false,
): MetricColorValue => {
  if (value === null) {
    return "default"
  }

  if (!targetCondition || !("target_float" in targetCondition)) {
    if (floatToBool) {
      switch (value) {
        case 1:
          return "success"
        case 0:
          return "danger"
        default:
          return "default"
      }
    } else {
      return "default"
    }
  }

  const { target_float, comparison_operator } = targetCondition

  switch (comparison_operator) {
    case FLOAT_COMPARISON_OPERATOR.GT:
      return value > target_float ? "success" : "danger"
    case FLOAT_COMPARISON_OPERATOR.GTE:
      return value >= target_float ? "success" : "danger"
    case FLOAT_COMPARISON_OPERATOR.LT:
      return value < target_float ? "success" : "danger"
    case FLOAT_COMPARISON_OPERATOR.LTE:
      return value <= target_float ? "success" : "danger"
    case FLOAT_COMPARISON_OPERATOR.EQ:
      return value === target_float ? "success" : "danger"
    case FLOAT_COMPARISON_OPERATOR.NEQ:
      return value !== target_float ? "success" : "danger"
    default:
      return "default"
  }
}

export const getMetricColor = (
  value: number | string,
  targetCondition?: TargetCondition,
  floatToBool = false,
): MetricColorValue => {
  if (typeof value === "number") {
    return getNumericMetricColor(value, targetCondition, floatToBool)
  }
  return getStringMetricColor(value, targetCondition)
}

export const getMetricColorClass = (color: MetricColorValue): string =>
  color === "success"
    ? "bg-coval-success-surface font-medium text-coval-success-on-surface"
    : color === "danger"
      ? "bg-coval-danger-surface font-medium text-coval-danger-on-surface"
      : ""

export const getMetricTextColorClass = (color: MetricColorValue): string =>
  color === "danger"
    ? "text-coval-danger-text"
    : color === "success"
      ? "text-coval-success-text"
      : "text-foreground"

/**
 * Determine pass/fail color from a resolved threshold.
 * Handles all operator types including range operators (bt/nbt) and binary float-to-bool.
 */
const getMetricColorFromThreshold = (
  value: number | string,
  threshold: ResolvedThreshold,
  metricType: ReturnType<typeof getMetricType>,
  floatToBool = false,
): MetricColorValue => {
  const { comparison_operator } = threshold

  if (metricType === METRIC_TYPE.NUMERICAL && typeof value === "number") {
    const upper = threshold.target_float_upper ?? 0
    const lower = threshold.target_float_lower ?? upper

    switch (comparison_operator) {
      case FLOAT_COMPARISON_OPERATOR.GT:
        return value > upper ? "success" : "danger"
      case FLOAT_COMPARISON_OPERATOR.GTE:
        return value >= upper ? "success" : "danger"
      case FLOAT_COMPARISON_OPERATOR.LT:
        return value < upper ? "success" : "danger"
      case FLOAT_COMPARISON_OPERATOR.LTE:
        return value <= upper ? "success" : "danger"
      case FLOAT_COMPARISON_OPERATOR.EQ:
        return value === upper ? "success" : "danger"
      case FLOAT_COMPARISON_OPERATOR.NEQ:
        return value !== upper ? "success" : "danger"
      case RANGE_COMPARISON_OPERATOR.BT:
        return value >= lower && value <= upper ? "success" : "danger"
      case RANGE_COMPARISON_OPERATOR.NBT:
        return value < lower || value > upper ? "success" : "danger"
      case "in":
      case "nin":
      default:
        return "default"
    }
  }

  if (metricType === METRIC_TYPE.BINARY) {
    // Binary metrics store float 0/1 — convert to YES/NO when floatToBool is set
    const strValue =
      floatToBool && typeof value === "number"
        ? value === 1
          ? ANNOTATION_VALUE.YES
          : value === 0
            ? ANNOTATION_VALUE.NO
            : String(value)
        : String(value)
    const targetValues = threshold.target_values ?? []
    const isIdeal =
      comparison_operator === SET_COMPARISON_OPERATOR.IN
        ? targetValues.includes(strValue)
        : !targetValues.includes(strValue)
    return strValue === ANNOTATION_VALUE.UNKNOWN
      ? "default"
      : isIdeal
        ? "success"
        : "danger"
  }

  if (
    (metricType === METRIC_TYPE.CATEGORICAL ||
      metricType === METRIC_TYPE.SET) &&
    typeof value === "string"
  ) {
    const targetValues = threshold.target_values ?? []
    const isIdeal =
      comparison_operator === SET_COMPARISON_OPERATOR.IN
        ? targetValues.includes(value)
        : !targetValues.includes(value)
    return isIdeal ? "success" : "danger"
  }

  return "default"
}

/**
 * Convenience wrapper: resolves the threshold from a metric and returns pass/fail color.
 * Falls back to legacy `getMetricColor(value, target_condition)` when no threshold is resolved.
 */
export const getMetricColorForMetric = (
  value: number | string,
  metric: MetricTypeSource & MetricThresholdResolutionSource,
  floatToBool = false,
): MetricColorValue => {
  const threshold = resolveThreshold(metric)
  if (!threshold) {
    return getMetricColor(
      value,
      metric.metric_metadata?.target_condition,
      floatToBool,
    )
  }
  const metricType = getMetricType(metric)
  return getMetricColorFromThreshold(value, threshold, metricType, floatToBool)
}
